import React, { useState } from "react";

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/v2/quotes`
  : "http://localhost:8880/v2/quotes";

function Pricing({ content }) {
  const { color, title, id } = content;
  const isMedia = id === "media";

  const emptyForm = { name: "", email: "", phone: "", company: "", subject: "", message: "" };
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setFormData(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError("Please fill in your name, email address, and message.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError(""); setLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        plan: isMedia ? (formData.subject.trim() || "Media Monitoring Request") : "Contact Us",
        message: formData.message.trim(),
        solution: title,
        type: isMedia ? "request" : "contact",
      };
      const resp = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (resp.ok) {
        setSuccess(true);
        setFormData(emptyForm);
        setTimeout(() => setSuccess(false), 6000);
      } else {
        const body = await resp.json().catch(() => ({}));
        setError(body.error || "Failed to send your request. Please try again.");
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === "AbortError") {
        setError("Request timed out. Please check your connection and try again.");
      } else {
        setError("Could not connect to the server. Please try again later.");
      }
    }
    setLoading(false);
  };

  const inputStyle = {
    width: "100%", padding: "18px 20px",
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "4px", color: "#fff",
    fontFamily: "'Readex Pro', monospace", fontSize: "14px",
    outline: "none", transition: "border-color 0.3s, box-shadow 0.3s",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block", marginBottom: "8px",
    fontSize: "10px", color: "rgba(255,255,255,0.35)",
    fontFamily: "monospace", letterSpacing: "3px", textTransform: "uppercase",
  };

  return (
    <section id="pricing" style={{
      padding: "120px 30px 160px", background: "#010108",
      position: "relative", overflow: "hidden"
    }}>
      <style>{`
        .iq-input:focus { border-color: ${color} !important; box-shadow: 0 0 20px ${color}25 !important; background: rgba(255,255,255,0.05) !important; }
        @keyframes floatAura { 0%,100%{transform:translateY(0) scale(1);} 50%{transform:translateY(-30px) scale(1.05);} }
      `}</style>

      <div style={{
        position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
        width: "700px", height: "700px",
        background: `radial-gradient(circle, ${color}06 0%, transparent 65%)`,
        animation: "floatAura 15s infinite ease-in-out", zIndex: 0, pointerEvents: "none"
      }} />

      <div style={{ maxWidth: "860px", margin: "0 auto", position: "relative", zIndex: 1 }}>

        <div style={{ textAlign: "center", marginBottom: "80px" }}>
          <span style={{ fontSize: "11px", color, fontFamily: "monospace", letterSpacing: "5px", fontWeight: "bold" }}>
            {isMedia ? "MEDIA_REQUEST_MODULE" : "CONTACT_UPLINK"}
          </span>
          <h2 style={{ fontSize: "clamp(32px, 4.5vw, 58px)", fontFamily: "'Conthrax', sans-serif", color: "#fff", marginTop: "24px", textTransform: "uppercase", lineHeight: 1.1 }}>
            {isMedia ? "Request a Demo" : "Contact Us"}
          </h2>
          <p style={{ color: "rgba(255,255,255,0.35)", fontFamily: "'Readex Pro', sans-serif", maxWidth: "560px", margin: "20px auto 0", lineHeight: 1.8, fontSize: "15px" }}>
            {isMedia
              ? "Interested in our Media Monitoring & OSINT platform? Leave your request below and we will get back to you within 24 hours."
              : "Have a question, a project idea, or want to explore how we can work together? We would love to hear from you."}
          </p>
        </div>

        <div style={{
          background: "rgba(255,255,255,0.012)",
          border: `1px solid rgba(255,255,255,0.07)`,
          borderRadius: "8px", padding: "60px 60px",
          boxShadow: `0 40px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)`,
          position: "relative"
        }}>
          <div style={{ position: "absolute", top: 0, left: "60px", right: "60px", height: "2px", background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
          <div style={{ position: "absolute", top: "20px", right: "24px", fontSize: "9px", color, fontFamily: "monospace", letterSpacing: "2px", opacity: 0.5 }}>
            {isMedia ? "REQUEST_NODE" : "CONTACT_NODE"}
          </div>

          {success ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ fontSize: "60px", marginBottom: "24px" }}>✅</div>
              <h3 style={{ color: "#fff", fontFamily: "'Conthrax', sans-serif", marginBottom: "16px" }}>
                {isMedia ? "Request Sent Successfully" : "Message Sent Successfully"}
              </h3>
              <p style={{ color: "rgba(255,255,255,0.4)", fontFamily: "'Readex Pro', sans-serif" }}>
                Our team will get back to you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-4">
                <div className="col-md-6">
                  <label style={labelStyle}>Full Name *</label>
                  <input className="iq-input" type="text" style={inputStyle} placeholder="Your full name" value={formData.name} onChange={e => set("name", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label style={labelStyle}>Email Address *</label>
                  <input className="iq-input" type="email" style={inputStyle} placeholder="email@company.com" value={formData.email} onChange={e => set("email", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label style={labelStyle}>Phone Number</label>
                  <input className="iq-input" type="tel" style={inputStyle} placeholder="+1 (000) 000-0000" value={formData.phone} onChange={e => set("phone", e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label style={labelStyle}>Organisation</label>
                  <input className="iq-input" type="text" style={inputStyle} placeholder="Your organisation name" value={formData.company} onChange={e => set("company", e.target.value)} />
                </div>
                {isMedia && (
                  <div className="col-12">
                    <label style={labelStyle}>Subject</label>
                    <input className="iq-input" type="text" style={inputStyle} placeholder="e.g. Demo request, Pricing inquiry, Partnership..." value={formData.subject} onChange={e => set("subject", e.target.value)} />
                  </div>
                )}
                <div className="col-12">
                  <label style={labelStyle}>{isMedia ? "Request Details *" : "Message *"}</label>
                  <textarea
                    className="iq-input"
                    style={{ ...inputStyle, minHeight: "160px", resize: "vertical", lineHeight: 1.7 }}
                    placeholder={isMedia
                      ? "Please describe your organisation's needs, the features you are interested in, expected data volumes, or any specific requirements..."
                      : "Write your question, idea, or message here..."}
                    value={formData.message}
                    onChange={e => set("message", e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <div className="col-12">
                    <div style={{ color: "#FF3366", fontFamily: "monospace", fontSize: "13px", padding: "12px 16px", background: "rgba(255,51,102,0.08)", border: "1px solid rgba(255,51,102,0.2)", borderRadius: "4px" }}>
                      {error}
                    </div>
                  </div>
                )}
                <div className="col-12" style={{ marginTop: "20px" }}>
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: "100%", padding: "22px",
                      background: loading ? "rgba(255,255,255,0.05)" : color,
                      border: `1px solid ${color}`, borderRadius: "4px",
                      color: "#fff", fontFamily: "'Conthrax', sans-serif", fontSize: "13px",
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "all 0.3s", letterSpacing: "2px",
                      boxShadow: loading ? "none" : `0 10px 40px ${color}30`,
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 20px 50px ${color}50`; } }}
                    onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = `0 10px 40px ${color}30`; }}
                  >
                    {loading ? "SENDING..." : (isMedia ? "SEND REQUEST" : "SEND MESSAGE")}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        <p style={{ textAlign: "center", marginTop: "32px", fontSize: "12px", color: "rgba(255,255,255,0.2)", fontFamily: "monospace" }}>
          Your information is kept confidential and will never be shared with third parties.
        </p>
      </div>
    </section>
  );
}

export default Pricing;
